/**
 * ClinicEditor — режим правки клиники: держит черновик пакетов и цену «сверх»,
 * валидацию и число врачей калькулятора. Таблица — в PackageEditor, справа —
 * живой калькулятор. Наружу отдаётся только запрос сохранения.
 */

import {useState} from 'react';

import {type ClinicPackage, clinicQuoteFor} from '@services/clients';
import type {SaveClinicInput} from '@services/tariffs';
import {formatNumber} from '@utils/format';

import {CalcIcon} from '../../CalcIcon';
import {SeatStepper} from '../../SeatStepper';
import {
  explainQuote,
  newDraft,
  type PackageDraft,
  toDrafts,
  validatePackages,
} from '../../Tariffs.utils';
import {PackageEditor} from '../PackageEditor';

type ClinicEditorProps = {
  packages: readonly ClinicPackage[];
  overPrice: number;
  calc: number;
  setCalc: (n: number) => void;
  onCancel: () => void;
  onAskSave: (input: SaveClinicInput) => void;
};

export const ClinicEditor = ({
  packages: initial,
  overPrice: initialOver,
  calc,
  setCalc,
  onCancel,
  onAskSave,
}: ClinicEditorProps) => {
  const [drafts, setDrafts] = useState<PackageDraft[]>(() => toDrafts(initial));
  const [overPrice, setOverPrice] = useState(String(initialOver));

  const patch = (id: string, part: Partial<PackageDraft>) =>
    setDrafts((ds) => ds.map((d) => (d.id === id ? {...d, ...part} : d)));
  const remove = (id: string) =>
    setDrafts((ds) => (ds.length <= 1 ? ds : ds.filter((d) => d.id !== id)));
  const add = () => setDrafts((ds) => [...ds, newDraft(ds[ds.length - 1])]);

  const valid = validatePackages(drafts);
  const packages: ClinicPackage[] = drafts.map(({name, upTo, price}) => ({name, upTo, price}));
  const over = Number(overPrice || 0);
  const quote = clinicQuoteFor(calc, packages, over);

  return (
    <div className="mt-5 grid grid-cols-[1fr_340px] items-start gap-[22px]">
      <PackageEditor
        drafts={drafts}
        overPrice={overPrice}
        setOverPrice={setOverPrice}
        onPatch={patch}
        onRemove={remove}
        onAdd={add}
        valid={valid}
        onCancel={onCancel}
        onSave={() => onAskSave({packages, overPrice: over})}
      />

      <div className="rounded-[13px] border border-skr-info-border bg-skr-info px-[17px] py-4">
        <div className="flex items-center gap-2 text-[13px] font-semibold text-skr-info-ink">
          <CalcIcon />
          Калькулятор
        </div>
        <div className="mt-3.5 flex items-center gap-[11px]">
          <span className="text-[12.5px] text-skr-info-ink2">Врачей</span>
          <SeatStepper seats={calc} setSeats={setCalc} size="md" />
        </div>
        <div className="mt-3.5 border-t border-skr-info-border pt-[13px] text-[12.5px] text-skr-info-ink2">
          {explainQuote(calc, packages, over)}
        </div>
        <div className="mt-3 flex items-baseline justify-between border-t border-skr-info-border pt-3">
          <span className="text-[12.5px] text-skr-info-ink2">Итого в месяц</span>
          <span>
            <span className="font-mono text-[22px] font-semibold text-skr-info-ink">
              {formatNumber(quote.sum)}
            </span>{' '}
            <span className="text-xs text-skr-info-faint">сом</span>
          </span>
        </div>
      </div>
    </div>
  );
};
